import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import "./custom-dropdown.scss";

export interface CustomDropdownOption {
  label: string;
  value: string;
}

export interface CustomDropdownProps {
  label?: string;
  options: CustomDropdownOption[];
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  onChange?: (value: string, option: CustomDropdownOption) => void;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  hint?: string;
  focusBorderWidth?: string;
  shouldUsePortal?: boolean;
  className?: string;
}

export const CustomDropdown: React.FC<CustomDropdownProps> = ({
  label,
  options,
  value: controlledValue,
  defaultValue,
  placeholder = "Select...",
  onChange,
  disabled = false,
  required = false,
  error,
  hint,
  focusBorderWidth,
  shouldUsePortal = false,
  className = "",
}) => {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue ?? "");
  const [isOpen, setIsOpen] = useState(false);
  const [dropDirection, setDropDirection] = useState<"down" | "up">("down");
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const activeValue = isControlled ? controlledValue : internalValue;
  const selectedOption = options.find((o) => o.value === activeValue);
  const displayLabel = selectedOption?.label ?? placeholder;

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !menuRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current.offsetHeight;
    const gap = 4;
    const spaceBelow = window.innerHeight - triggerRect.bottom - gap;
    const direction = spaceBelow < menuHeight ? "up" : "down";
    setDropDirection(direction);

    if (shouldUsePortal) {
      const top =
        direction === "down"
          ? triggerRect.bottom + gap
          : triggerRect.top - gap - menuHeight;

      setPortalStyle({
        position: "fixed",
        top,
        left: triggerRect.left,
        width: triggerRect.width,
      });
    }
  }, [shouldUsePortal]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideContainer =
        containerRef.current?.contains(target) ?? false;
      const insideMenu = menuRef.current?.contains(target) ?? false;

      if (!insideContainer && !insideMenu) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    updatePosition();

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, updatePosition]);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (option: CustomDropdownOption) => {
    if (!isControlled) {
      setInternalValue(option.value);
    }
    onChange?.(option.value, option);
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      setIsOpen(false);
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        const firstItem = menuRef.current?.querySelector<HTMLLIElement>(
          ".custom-dropdown__option"
        );
        firstItem?.focus();
      }
    }
  };

  const handleOptionKeyDown = (
    event: React.KeyboardEvent,
    option: CustomDropdownOption,
    index: number
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleSelect(option);
      return;
    }

    if (event.key === "Escape") {
      setIsOpen(false);
      triggerRef.current?.focus();
      return;
    }

    const items = menuRef.current?.querySelectorAll<HTMLLIElement>(
      ".custom-dropdown__option"
    );
    if (!items) return;

    let nextIndex = -1;
    if (event.key === "ArrowDown") {
      nextIndex = (index + 1) % items.length;
    } else if (event.key === "ArrowUp") {
      nextIndex = (index - 1 + items.length) % items.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = items.length - 1;
    }

    if (nextIndex >= 0) {
      event.preventDefault();
      items[nextIndex]?.focus();
    }
  };

  const containerStyle: React.CSSProperties | undefined = focusBorderWidth
    ? ({ "--custom-dropdown-focus-border-width": focusBorderWidth } as React.CSSProperties)
    : undefined;

  return (
    <div
      className={`custom-dropdown ${error ? "custom-dropdown--error" : ""} ${className}`}
      ref={containerRef}
      style={containerStyle}
    >
      {label && (
        <label className="custom-dropdown__label">
          {label}
          {required && <span className="custom-dropdown__required"> *</span>}
        </label>
      )}

      <div className="custom-dropdown__wrapper">
        <button
          ref={triggerRef}
          className={`custom-dropdown__trigger ${isOpen ? "custom-dropdown__trigger--open" : ""} ${!selectedOption ? "custom-dropdown__trigger--placeholder" : ""} ${error ? "custom-dropdown__trigger--error" : ""}`}
          type="button"
          onClick={handleToggle}
          onKeyDown={handleTriggerKeyDown}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label={label}
          aria-invalid={!!error}
        >
          <span className="custom-dropdown__value">{displayLabel}</span>
          <span className="custom-dropdown__icon" aria-hidden="true">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 9L12 15L18 9"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>

        {isOpen && !shouldUsePortal && (
          <ul
            ref={menuRef}
            className={`custom-dropdown__menu custom-dropdown__menu--${dropDirection}`}
            role="listbox"
            aria-label={label}
          >
            {options.map((option, index) => (
              <li
                key={option.value}
                className={`custom-dropdown__option ${option.value === activeValue ? "custom-dropdown__option--selected" : ""}`}
                role="option"
                aria-selected={option.value === activeValue}
                tabIndex={0}
                onClick={() => handleSelect(option)}
                onKeyDown={(e) => handleOptionKeyDown(e, option, index)}
              >
                <span className="custom-dropdown__option-label">
                  {option.label}
                </span>
                {option.value === activeValue && (
                  <span
                    className="custom-dropdown__check"
                    aria-hidden="true"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M16.6667 5L7.5 14.1667L3.33334 10"
                        stroke="#1F1F1F"
                        strokeWidth="1.66667"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {isOpen &&
        shouldUsePortal &&
        createPortal(
          <ul
            ref={menuRef}
            className="custom-dropdown__menu custom-dropdown__menu--portal"
            role="listbox"
            aria-label={label}
            style={portalStyle}
          >
            {options.map((option, index) => (
              <li
                key={option.value}
                className={`custom-dropdown__option ${option.value === activeValue ? "custom-dropdown__option--selected" : ""}`}
                role="option"
                aria-selected={option.value === activeValue}
                tabIndex={0}
                onClick={() => handleSelect(option)}
                onKeyDown={(e) => handleOptionKeyDown(e, option, index)}
              >
                <span className="custom-dropdown__option-label">
                  {option.label}
                </span>
                {option.value === activeValue && (
                  <span
                    className="custom-dropdown__check"
                    aria-hidden="true"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M16.6667 5L7.5 14.1667L3.33334 10"
                        stroke="#1F1F1F"
                        strokeWidth="1.66667"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                )}
              </li>
            ))}
          </ul>,
          document.body
        )}

      {hint && !error && (
        <div className="custom-dropdown__hint">{hint}</div>
      )}
      {error && <div className="custom-dropdown__error">{error}</div>}
    </div>
  );
};
